import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, ArrowLeft, Zap } from "lucide-react";

interface JobWizardStep4Props {
  onNext: (selectedSkills: string[]) => void;
  onBack: () => void;
  skills: string[];
}

export function JobWizardStep4({ onNext, onBack, skills }: JobWizardStep4Props) {
  const [selectedSkills, setSelectedSkills] = useState<string[]>(
    [...skills] // All pre-selected by default
  );

  const handleToggleSkill = (skill: string) => {
    setSelectedSkills(prev => 
      prev.includes(skill)
        ? prev.filter(s => s !== skill)
        : [...prev, skill]
    );
  };

  const handleNext = () => {
    onNext(selectedSkills);
  };

  return (
    <div className="min-h-screen bg-blue-100 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg">
        <div className="bg-gradient-to-r from-accent-light/30 to-primary-light/20 border-b border-border shadow-sm rounded-t-lg backdrop-blur-sm">
          <div className="p-6 border-b border-border-light">
            <div className="flex items-center justify-between">
              <div>
                <Badge variant="outline" className="mb-2">Step 4 of 5</Badge>
                <h1 className="flex items-center gap-2 text-xl font-semibold text-foreground">
                  <Zap className="w-5 h-5 text-success" />
                  Select Keywords & Skills
                </h1>
                <p className="text-muted-foreground">
                  Choose the skills and keywords that represent your expertise
                </p>
              </div>
              <Badge variant="secondary" className="text-lg px-3 py-1">
                {selectedSkills.length} / {skills.length} selected
              </Badge>
            </div>
            <div className="mt-4">
              <Progress value={80} className="w-full" />
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-4 mb-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Skills & Keywords</h3>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedSkills([...skills])}
                >
                  Select All
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedSkills([])}
                >
                  Deselect All
                </Button>
              </div>
            </div>
            <p className="text-muted-foreground">
              All skills are pre-selected. Uncheck any that don't match your experience or capabilities.
            </p>
          </div>

          <div className="grid gap-3">
            {skills.map((skill, index) => (
              <div 
                key={index}
                className={`flex items-start gap-3 p-4 rounded-lg border transition-all cursor-pointer hover:border-success ${
                  selectedSkills.includes(skill) 
                    ? 'border-success bg-success-light' 
                    : 'border-border-light'
                }`}
                onClick={() => handleToggleSkill(skill)}
              >
                <Checkbox 
                  checked={selectedSkills.includes(skill)}
                  onChange={() => handleToggleSkill(skill)}
                  className="mt-1"
                />
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      #{index + 1}
                    </Badge>
                  </div>
                  <p className="text-sm leading-relaxed">
                    {skill}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between pt-6 border-t border-border-light mt-8">
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <Button 
              variant="gradient" 
              onClick={handleNext}
              disabled={selectedSkills.length === 0}
            >
              Save to Library
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}